import { useState, forwardRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { API, LOCALITIES, IMAGES } from "../constants";
import { Reveal } from "./Reveal";

const formatError = (detail) => {
    if (!detail) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
    return String(detail);
};

const inputCls =
    "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3.5 text-sm sm:text-base text-slate-900 placeholder:text-slate-400 outline-none transition-all duration-200 focus:border-[#0B5CF5] focus:ring-4 focus:ring-[#0B5CF5]/10";

const EarlyAccess = forwardRef((_, ref) => {
    const [form, setForm] = useState({ name: "", mobile: "", locality: "", email: "" });
    const [consent, setConsent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(null);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const payload = {
                name: form.name,
                mobile: form.mobile,
                locality: form.locality,
                consent,
            };
            if (form.email.trim()) payload.email = form.email.trim();
            const { data } = await axios.post(`${API}/leads`, payload);
            setSuccess(data);
        } catch (err) {
            setError(formatError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="early-access" ref={ref} className="py-16 sm:py-24 lg:py-32 bg-[#0F172A] relative overflow-hidden grain" data-testid="early-access-section">
            <img
                src={IMAGES.sinkClean}
                alt=""
                aria-hidden="true"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover opacity-[0.08]"
            />
            <div
                className="pointer-events-none absolute -bottom-32 -left-32 w-[30rem] h-[30rem] rounded-full"
                style={{ background: "radial-gradient(circle, rgba(11,92,245,0.35), transparent 65%)" }}
            />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                <div>
                    <Reveal>
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#7DA8FF] mb-4">Early access</p>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <h2 className="font-display font-medium text-white text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-[1.1]">
                            Be among the first to <span className="italic text-[#38BDF8]">experience Sevano.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.16}>
                        <p className="mt-5 text-base sm:text-lg text-slate-400 leading-relaxed max-w-lg">
                            Join the Agartala early access list. We'll let you know the moment bookings open
                            in your locality — no spam, ever.
                        </p>
                    </Reveal>
                    <Reveal delay={0.24}>
                        <div className="mt-8 flex items-center gap-3 text-sm text-slate-400">
                            <ShieldCheck size={18} className="text-[#38BDF8] shrink-0" />
                            <span>Your details are used only for the Sevano launch. We never sell or share your data.</span>
                        </div>
                    </Reveal>
                </div>

                <Reveal delay={0.1}>
                    <div className="rounded-[2rem] bg-white p-6 sm:p-10 shadow-[0_40px_90px_-30px_rgba(11,92,245,0.5)]">
                        <AnimatePresence mode="wait">
                            {success ? (
                                <motion.div
                                    key="success"
                                    initial={{ opacity: 0, scale: 0.96 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                    className="text-center py-8"
                                    data-testid="early-access-success-message"
                                >
                                    <div className="w-16 h-16 mx-auto rounded-full bg-[#EBF2FF] flex items-center justify-center mb-5">
                                        <CheckCircle2 size={32} className="text-[#0B5CF5]" />
                                    </div>
                                    <h3 className="text-2xl font-extrabold text-slate-900 tracking-tight">You're on the list</h3>
                                    <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed max-w-sm mx-auto">
                                        {success.message}
                                    </p>
                                    <p className="mt-4 text-xs text-slate-400">
                                        Reference ID: <span className="font-mono">{success.lead_id.slice(0, 8).toUpperCase()}</span>
                                    </p>
                                </motion.div>
                            ) : (
                                <motion.form
                                    key="form"
                                    onSubmit={submit}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0, y: -12 }}
                                    className="flex flex-col gap-4"
                                    data-testid="early-access-form"
                                    noValidate
                                >
                                    <div>
                                        <label htmlFor="ea-name" className="block text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">
                                            Full name
                                        </label>
                                        <input
                                            id="ea-name"
                                            data-testid="early-access-name-input"
                                            type="text"
                                            required
                                            value={form.name}
                                            onChange={set("name")}
                                            placeholder="e.g. Rahul Saha"
                                            className={inputCls}
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="ea-mobile" className="block text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">
                                            Mobile number
                                        </label>
                                        <input
                                            id="ea-mobile"
                                            data-testid="early-access-mobile-input"
                                            type="tel"
                                            required
                                            value={form.mobile}
                                            onChange={set("mobile")}
                                            placeholder="+91 98765 43210"
                                            className={inputCls}
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="ea-locality" className="block text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">
                                            Your locality in Agartala
                                        </label>
                                        <select
                                            id="ea-locality"
                                            data-testid="early-access-locality-select"
                                            required
                                            value={form.locality}
                                            onChange={set("locality")}
                                            className={`${inputCls} ${form.locality ? "" : "text-slate-400"}`}
                                        >
                                            <option value="" disabled>
                                                Select your locality
                                            </option>
                                            {LOCALITIES.map((l) => (
                                                <option key={l} value={l}>
                                                    {l}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="ea-email" className="block text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">
                                            Email <span className="normal-case font-medium text-slate-400">(optional)</span>
                                        </label>
                                        <input
                                            id="ea-email"
                                            data-testid="early-access-email-input"
                                            type="email"
                                            value={form.email}
                                            onChange={set("email")}
                                            placeholder="rahulsaha@example.com"
                                            className={inputCls}
                                        />
                                    </div>

                                    <label className="flex items-start gap-3 cursor-pointer select-none mt-1">
                                        <input
                                            type="checkbox"
                                            data-testid="early-access-consent-checkbox"
                                            checked={consent}
                                            onChange={(e) => setConsent(e.target.checked)}
                                            className="mt-0.5 w-[18px] h-[18px] rounded accent-[#0B5CF5]"
                                        />
                                        <span className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                                            I agree to receive launch updates from Sevano via SMS, call or WhatsApp.
                                        </span>
                                    </label>

                                    <AnimatePresence>
                                        {error && (
                                            <motion.p
                                                initial={{ opacity: 0, y: -6 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0 }}
                                                className="text-sm font-semibold text-red-600 bg-red-50 rounded-xl px-4 py-3"
                                                data-testid="early-access-error-message"
                                                role="alert"
                                            >
                                                {error}
                                            </motion.p>
                                        )}
                                    </AnimatePresence>

                                    <button
                                        type="submit"
                                        data-testid="early-access-submit-button"
                                        disabled={loading}
                                        className="btn-primary inline-flex items-center justify-center gap-2 px-7 py-4 text-sm sm:text-base disabled:opacity-60 disabled:cursor-not-allowed mt-1"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 size={18} className="animate-spin" /> Joining…
                                            </>
                                        ) : (
                                            <>
                                                Join the Early Access List <ArrowRight size={18} />
                                            </>
                                        )}
                                    </button>
                                    <p className="text-[11px] text-slate-400 text-center leading-relaxed">
                                        We respect your privacy. Zero spam. Your details are used only for the
                                        Sevano Agartala launch.
                                    </p>
                                </motion.form>
                            )}
                        </AnimatePresence>
                    </div>
                </Reveal>
            </div>
        </section>
    );
});

EarlyAccess.displayName = "EarlyAccess";
export default EarlyAccess;
